import { OnInit, OnDestroy, inject } from '@angular/core';
import { ElementRef, HostListener } from '@angular/core';
import { Signal, effect, Component } from '@angular/core';

import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';

import { WorkspaceFacade } from '@state';
import { Project, Organization, OrganizationProjects } from '@models';

@Component({
  selector: 'app-project-selector',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './project-selector.component.html',
  styleUrls: ['./project-selector.component.scss'],
})
export class ProjectSelectorComponent implements OnInit, OnDestroy {
  isMenuOpen = false;
  expandedOrgIds = new Set<string>();
  selectedProject: Project | null = null;
  isCollapsed = false;

  private idleTimeoutId: any = null;
  private readonly collapseDelayMs = 3000;

  projects: Signal<Project[]>;
  organizations: Signal<Organization[]>;
  orgProjects: Signal<OrganizationProjects[]>;

  constructor(
    private store: WorkspaceFacade,
    private router: Router,
    private elementRef: ElementRef
  ) {
    this.projects = this.store.projects;
    this.orgProjects = this.store.orgProjects;
    this.organizations = this.store.organizations;

    this.syncSelectedProjectFromStore();
    this.syncExpandedOrganizations();
  }

  ngOnInit(): void {
    this.startIdleTimer();
  }

  ngOnDestroy(): void {
    this.clearIdleTimer();
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
    this.expand();
    if (this.isMenuOpen) {
      this.clearIdleTimer();
    } else {
      this.startIdleTimer();
    }
  }

  selectProject(project: Project): void {
    this.selectedProject = project;
    this.isMenuOpen = false;
    this.expand();
    this.startIdleTimer();
    const orgId = this.orgProjects().find((op) => op.projectId === project.id)?.organizationId ?? null;
    this.store.selectProject(project.id, orgId);
  }

  getProjectsForOrganization(orgId: string): Project[] {
    const projectIds = this.orgProjects()
      .filter((op) => op.organizationId === orgId)
      .map((op) => op.projectId);
    return this.projects().filter((p) => projectIds.includes(p.id));
  }

  getUnmappedProjects(): Project[] {
    const mapped = new Set(this.orgProjects().map((op) => op.projectId));
    return this.projects().filter((p) => !mapped.has(p.id));
  }

  toggleOrg(orgId: string): void {
    if (this.expandedOrgIds.has(orgId)) {
      this.expandedOrgIds.delete(orgId);
    } else {
      this.expandedOrgIds.add(orgId);
    }
    this.expand();
    this.startIdleTimer();
  }

  isOrgExpanded(orgId: string): boolean {
    return this.expandedOrgIds.has(orgId);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isMenuOpen) return;
    const target = event.target as HTMLElement;
    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.isMenuOpen = false;
      this.expand();
      this.startIdleTimer();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isMenuOpen) {
      this.isMenuOpen = false;
      this.expand();
      this.startIdleTimer();
    }
  }

  onMouseEnterButton(): void {
    this.expand();
    this.clearIdleTimer();
  }

  onMouseLeaveButton(): void {
    if (!this.isMenuOpen) this.startIdleTimer();
  }

  private expand(): void {
    this.isCollapsed = false;
  }

  private collapse(): void {
    if (this.isMenuOpen) return;
    this.isCollapsed = true;
  }

  private startIdleTimer(): void {
    this.clearIdleTimer();
    this.idleTimeoutId = setTimeout(() => {
      this.collapse();
    }, this.collapseDelayMs);
  }

  private clearIdleTimer(): void {
    if (this.idleTimeoutId) {
      clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }
  }

  private syncSelectedProjectFromStore(): void {
    effect(() => {
      const selectedId = this.store.selectedProjectId();
      const next = selectedId
        ? this.projects().find((p) => p.id === selectedId) ?? null
        : null;
      this.selectedProject = next;
    });
  }

  private syncExpandedOrganizations(): void {
    effect(() => {
      const orgs = this.organizations();
      const selectedOrgId = this.store.selectedOrganizationId();
      const selectedProjectId = this.store.selectedProjectId();
      const mappings = this.orgProjects();

      if (orgs.length === 1) {
        this.expandedOrgIds.add(orgs[0].id);
      }

      if (selectedOrgId) {
        this.expandedOrgIds.add(selectedOrgId);
      }

      if (selectedProjectId) {
        const mappedOrgId = mappings.find((op) => op.projectId === selectedProjectId)?.organizationId ?? null;
        if (mappedOrgId) this.expandedOrgIds.add(mappedOrgId);
      }
    });
  }

  navToNewProject(): void {
    this.router.navigate(['/create-project'], {
      queryParams: { from: 'home' },
    });
  }

  navToNewOrganization(): void {
    this.router.navigate(['/create-organization'], {
      queryParams: { from: 'home' },
    });
  }
}
